import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import StatsPie from '../components/StatsPie';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  Search,
  RefreshCw,
  Wifi,
  WifiOff,
  Radio,
  CircleDashed,
  ChevronLeft,
  ChevronRight,
  Server,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Download,
  Zap,
  Power,
  BarChart3,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('id-ID', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
};

const formatAge = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'baru saja';
  if (min < 60) return `${min}m yang lalu`;
  if (min < 1440) return `${Math.round(min / 60)}j yang lalu`;
  return `${Math.round(min / 1440)}h yang lalu`;
};

const formatPower = (val) => {
  if (val === null || val === undefined) return '-';
  const n = Number(val);
  if (!isFinite(n)) return '-';
  return `${n.toFixed(2)} dBm`;
};

const ACSMonitoring = () => {
  const { token } = useAuth();
  const { selectedBranch, selectedRouter } = useApp();

  const [data, setData] = useState([]);
  const [stats, setStats] = useState({ all: 0, online: 0, offline: 0, terdaftar: 0, belum: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | online | offline | terdaftar | belum
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [stats2, setStats2] = useState({ status: [], optical_rx: [], device_brand: [], top_models: [], total: 0 });
  const [showStats, setShowStats] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // holds device_id currently running

  const fetchData = useCallback(async (showToast = false) => {
    if (!token) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('status', statusFilter);
      params.append('limit', limit.toString());
      params.append('offset', ((page - 1) * limit).toString());
      if (search) params.append('search', search);
      if (selectedBranch && selectedBranch !== 'all') params.append('branch_id', selectedBranch);
      if (selectedRouter && selectedRouter !== 'all') params.append('router_id', selectedRouter);

      const resp = await axios.get(`${API_URL}/api/v2/acs-monitoring?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data && resp.data.success) {
        setData(resp.data.data || []);
        setTotal(resp.data.total || 0);
        setStats(resp.data.stats || stats);
        setLastUpdated(new Date());
        if (showToast) toast.success('Data ACS diperbarui');
      }
    } catch (err) {
      toast.error('Gagal memuat data ACS');
      console.error('ACS monitoring error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, page, limit, search, selectedBranch, selectedRouter]); // eslint-disable-line

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== 'all') params.append('branch_id', selectedBranch);
      if (selectedRouter && selectedRouter !== 'all') params.append('router_id', selectedRouter);
      const resp = await axios.get(`${API_URL}/api/v2/acs-monitoring/stats?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data && resp.data.success) {
        setStats2({
          status: resp.data.status || [],
          optical_rx: resp.data.optical_rx || [],
          device_brand: resp.data.device_brand || [],
          top_models: resp.data.top_models || [],
          total: resp.data.total || 0,
        });
      }
    } catch (err) {
      console.error('ACS stats error:', err);
    }
  }, [token, selectedBranch, selectedRouter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    setPage(1);
    fetchData();
  }, [statusFilter, search, selectedBranch, selectedRouter]); // eslint-disable-line

  useEffect(() => {
    fetchData();
  }, [page]); // eslint-disable-line

  // Debounce search: setSearch 500ms after last keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchTerm.trim());
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const handleRefresh = () => {
    fetchData(true);
  };

  const handleExportCsv = async () => {
    if (!token) return;
    try {
      const params = new URLSearchParams();
      params.append('status', statusFilter);
      if (search) params.append('search', search);
      if (selectedBranch && selectedBranch !== 'all') params.append('branch_id', selectedBranch);
      if (selectedRouter && selectedRouter !== 'all') params.append('router_id', selectedRouter);
      const resp = await axios.get(`${API_URL}/api/v2/acs-monitoring/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:T\.]/g, '-').slice(0, 19);
      a.download = `acs_monitoring_${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV berhasil di-download');
    } catch (err) {
      toast.error('Export CSV gagal');
      console.error(err);
    }
  };

  const handleDeviceAction = async (deviceId, operation, customerName) => {
    if (!deviceId) {
      toast.error('Device belum terdaftar di ACS');
      return;
    }
    const labels = { reboot: 'Reboot ONT', resync: 'Force Inform' };
    if (operation === 'reboot' && !window.confirm(`Reboot ONT ${customerName}? Koneksi customer akan terputus sementara.`)) return;
    try {
      setActionLoading(deviceId + ':' + operation);
      const resp = await axios.post(
        `${API_URL}/api/v2/acs-monitoring/device/${encodeURIComponent(deviceId)}/operation?operation=${operation}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data && resp.data.success) {
        toast.success(`${labels[operation]} terkirim ke ONT ${customerName}`);
      } else {
        toast.error(`${labels[operation]} gagal`);
      }
    } catch (err) {
      toast.error(`${labels[operation]} error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const renderStateBadge = (state, last_inform) => {
    if (state === 'online') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" title={`Online · last inform ${formatAge(last_inform)}`} data-testid="badge-online">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>Online
        </span>
      );
    }
    if (state === 'offline') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" title={`Offline · last inform ${formatAge(last_inform)}`} data-testid="badge-offline">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>Offline
        </span>
      );
    }
    if (state === 'terdaftar') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" title="ACS terdaftar (idle / belum inform)" data-testid="badge-terdaftar">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>Terdaftar
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-gray-400" title="Belum terdaftar di GenieACS" data-testid="badge-belum">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>Belum Install
      </span>
    );
  };

  const StatChip = ({ id, label, shortLabel, count, icon: Icon, activeClass, idleClass }) => {
    const active = statusFilter === id;
    return (
      <button
        onClick={() => setStatusFilter(id)}
        data-testid={`acs-filter-${id}`}
        className={`flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 rounded-lg border text-xs sm:text-sm font-medium transition-all min-w-0 ${active ? activeClass : idleClass}`}
      >
        <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
        <span className="hidden sm:inline truncate">{label}</span>
        <span className="sm:hidden truncate">{shortLabel || label}</span>
        <span className={`px-1.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold shrink-0 ${active ? 'bg-white/20' : 'bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-gray-200'}`}>
          {(count || 0).toLocaleString('id-ID')}
        </span>
      </button>
    );
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4 min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Server className="w-6 h-6 sm:w-7 sm:h-7 text-violet-600 dark:text-violet-400" />
              <span>ACS Monitoring</span>
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Status GenieACS / TR-069 per pelanggan PPPoE · {lastUpdated && `Update: ${formatDateTime(lastUpdated)}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowStats((v) => !v)}
              data-testid="acs-toggle-stats"
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 border dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              title={showStats ? 'Sembunyikan statistik' : 'Tampilkan statistik'}
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden md:inline">Statistik</span>
              {showStats ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <button
              onClick={handleExportCsv}
              data-testid="acs-export-csv"
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 border dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              title="Export ke CSV (untuk teknisi field)"
            >
              <Download className="w-4 h-4" />
              <span className="hidden md:inline">CSV</span>
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              data-testid="acs-refresh-btn"
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistik Panel */}
      {showStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 items-stretch" style={{gridAutoRows: '1fr'}} data-testid="acs-stats-grid">
          <StatsPie title="Status" data={stats2.status} total={stats2.total} />
          <StatsPie title="Optical RX" data={stats2.optical_rx} />
          <StatsPie title="Merk Perangkat" data={stats2.device_brand} total={stats2.total} />
          <StatsPie title="Top ONT Model" data={stats2.top_models} />
        </div>
      )}

      {/* Filter Chips */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 sm:gap-2">
        <StatChip
          id="all" label="Semua" shortLabel="Semua" count={stats.all || 0} icon={Radio}
          activeClass="bg-violet-600 text-white border-violet-600"
          idleClass="bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-slate-600 hover:border-violet-400"
        />
        <StatChip
          id="online" label="ACS Online" shortLabel="Online" count={stats.online || 0} icon={Wifi}
          activeClass="bg-green-600 text-white border-green-600"
          idleClass="bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-slate-600 hover:border-green-400"
        />
        <StatChip
          id="offline" label="ACS Offline" shortLabel="Offline" count={stats.offline || 0} icon={WifiOff}
          activeClass="bg-amber-600 text-white border-amber-600"
          idleClass="bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-slate-600 hover:border-amber-400"
        />
        <StatChip
          id="terdaftar" label="Terdaftar" shortLabel="Idle" count={stats.terdaftar || 0} icon={CheckCircle2}
          activeClass="bg-blue-600 text-white border-blue-600"
          idleClass="bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-slate-600 hover:border-blue-400"
        />
        <StatChip
          id="belum" label="Belum Install" shortLabel="Belum" count={stats.belum || 0} icon={CircleDashed}
          activeClass="bg-gray-700 text-white border-gray-700"
          idleClass="bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-slate-600 hover:border-gray-500"
        />
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Cari nama, username PPPoE, no. akun..."
          data-testid="acs-search-input"
          className="w-full pl-10 pr-10 py-2 border dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 bg-white dark:bg-slate-700 dark:text-white text-sm"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            data-testid="acs-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
            title="Bersihkan pencarian"
          >
            <XCircle className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Desktop Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Pelanggan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Cabang</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status ACS</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">IP / Remote</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">ONT Model</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Rx Power</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Last Inform</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Device ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Aksi</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Memuat data ACS...
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    <Server className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    Tidak ada data
                  </td>
                </tr>
              ) : (
                data.map((row, idx) => (
                  <tr key={row.id || idx} className="hover:bg-gray-50 dark:hover:bg-slate-700" data-testid={`acs-row-${idx}`}>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{row.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{row.account_number}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-gray-900 dark:text-white">{row.pppoe_username}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{row.branch_name}</td>
                    <td className="px-4 py-3">{renderStateBadge(row.acs_state, row.acs_last_inform)}</td>
                    <td className="px-4 py-3">
                      {row.current_ip && row.current_ip !== '-' ? (
                        <a
                          href={`http://${row.current_ip}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-mono text-xs text-violet-600 hover:text-violet-800 hover:underline dark:text-violet-400"
                          title={`Remote ONT: http://${row.current_ip}`}
                          data-testid={`acs-remote-ip-${idx}`}
                        >
                          {row.current_ip}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">{row.ont_model || '-'}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-600 dark:text-gray-300">{formatPower(row.ont_rx_power)}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">{formatAge(row.acs_last_inform)}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 break-all">{row.acs_device_id || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {row.acs_device_id ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleDeviceAction(row.acs_device_id, 'resync', row.name)}
                            disabled={actionLoading === row.acs_device_id + ':resync'}
                            data-testid={`acs-resync-${idx}`}
                            title="Force Inform (Refresh device parameters)"
                            className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 disabled:opacity-50"
                          >
                            <Zap className={`w-3.5 h-3.5 ${actionLoading === row.acs_device_id + ':resync' ? 'animate-pulse' : ''}`} />
                          </button>
                          <button
                            onClick={() => handleDeviceAction(row.acs_device_id, 'reboot', row.name)}
                            disabled={actionLoading === row.acs_device_id + ':reboot'}
                            data-testid={`acs-reboot-${idx}`}
                            title="Reboot ONT (koneksi customer terputus sementara)"
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 disabled:opacity-50"
                          >
                            <Power className={`w-3.5 h-3.5 ${actionLoading === row.acs_device_id + ':reboot' ? 'animate-pulse' : ''}`} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-[11px] text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-slate-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Memuat data...
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-slate-400">
            <Server className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            Tidak ada data
          </div>
        ) : (
          data.map((row, idx) => (
            <div key={row.id || idx} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-3 space-y-2" data-testid={`acs-card-${idx}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{row.name}</div>
                  <div className="text-xs font-mono text-gray-500 dark:text-gray-400">{row.pppoe_username}</div>
                </div>
                {renderStateBadge(row.acs_state, row.acs_last_inform)}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Cabang</div>
                  <div className="text-gray-800 dark:text-gray-200">{row.branch_name}</div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">IP / Remote</div>
                  {row.current_ip && row.current_ip !== '-' ? (
                    <a
                      href={`http://${row.current_ip}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-violet-600 hover:underline dark:text-violet-400"
                      data-testid={`acs-remote-ip-m-${idx}`}
                    >
                      {row.current_ip}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">ONT Model</div>
                  <div className="text-gray-800 dark:text-gray-200">{row.ont_model || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Rx Power</div>
                  <div className="text-gray-800 dark:text-gray-200 font-mono">{formatPower(row.ont_rx_power)}</div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Last Inform</div>
                  <div className="text-gray-800 dark:text-gray-200">{formatAge(row.acs_last_inform)}</div>
                </div>
              </div>
              {row.acs_device_id && (
                <>
                  <div className="pt-1 border-t dark:border-slate-700">
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">Device ID</div>
                    <div className="text-[11px] font-mono text-gray-600 dark:text-gray-300 break-all">{row.acs_device_id}</div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => handleDeviceAction(row.acs_device_id, 'resync', row.name)}
                      disabled={actionLoading === row.acs_device_id + ':resync'}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs disabled:opacity-50"
                      data-testid={`acs-resync-m-${idx}`}
                    >
                      <Zap className="w-3.5 h-3.5" />Force Inform
                    </button>
                    <button
                      onClick={() => handleDeviceAction(row.acs_device_id, 'reboot', row.name)}
                      disabled={actionLoading === row.acs_device_id + ':reboot'}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs disabled:opacity-50"
                      data-testid={`acs-reboot-m-${idx}`}
                    >
                      <Power className="w-3.5 h-3.5" />Reboot
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
          <div>
            Halaman {page} dari {totalPages} · Total {total.toLocaleString('id-ID')} record
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              data-testid="acs-prev-btn"
              className="px-3 py-1.5 border dark:border-slate-600 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              data-testid="acs-next-btn"
              className="px-3 py-1.5 border dark:border-slate-600 rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ACSMonitoring;
